import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, of, switchMap } from 'rxjs';
import { DonationService } from '../../services/donation.service';
import { DonationAssignmentService } from '../../services/donation-assignment.service';
import { DonationsApiActions, DonationsPageActions } from './donations.actions';

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

@Injectable()
export class DonationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly donationService = inject(DonationService);
  private readonly assignmentService = inject(DonationAssignmentService);

  readonly loadDonations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DonationsPageActions.opened,
        DonationsPageActions.filtersChanged,
        DonationsPageActions.pageChanged,
      ),
      switchMap((action) => {
        const filters = 'filters' in action
          ? action.filters
          : { page: action.page, pageSize: action.pageSize };

        return this.donationService.getAll(filters).pipe(
          map((response) => DonationsApiActions.loadSucceeded({
            donations: response.items ?? [],
            total: response.total ?? 0,
            totalAmount: response.totalAmount ?? 0,
          })),
          catchError((error: { message?: string }) => of(
            DonationsApiActions.loadFailed({ error: error?.message ?? 'Unable to load donations' }),
          )),
        );
      }),
    ),
  );

  readonly createDonation$ = createEffect(() => this.actions$.pipe(
    ofType(DonationsPageActions.createRequested),
    concatMap(({ requestId, donation }) => this.donationService.create(donation).pipe(
      map((created) => DonationsApiActions.createSucceeded({ requestId, donation: created })),
      catchError((error: unknown) => of(DonationsApiActions.createFailed({
        requestId,
        error: errorMessage(error, 'Unable to create donation'),
      }))),
    )),
  ));

  readonly createInKindDonation$ = createEffect(() => this.actions$.pipe(
    ofType(DonationsPageActions.createInKindRequested),
    concatMap(({ requestId, donation }) => this.donationService.createInKind(donation).pipe(
      map((created) => DonationsApiActions.createInKindSucceeded({ requestId, donation: created })),
      catchError((error: unknown) => of(DonationsApiActions.createInKindFailed({
        requestId,
        error: errorMessage(error, 'Unable to create in-kind donation'),
      }))),
    )),
  ));

  readonly updateDonationStatus$ = createEffect(() => this.actions$.pipe(
    ofType(DonationsPageActions.statusUpdateRequested),
    concatMap(({ requestId, donationId, update }) => this.donationService.updateStatus(donationId, update).pipe(
      map((donation) => DonationsApiActions.statusUpdateSucceeded({ requestId, donation })),
      catchError((error: unknown) => of(DonationsApiActions.statusUpdateFailed({
        requestId,
        error: errorMessage(error, 'Unable to update donation status'),
      }))),
    )),
  ));

  readonly assignDonation$ = createEffect(() => this.actions$.pipe(
    ofType(DonationsPageActions.assignmentRequested),
    concatMap(({ requestId, donationId, assignment }) => this.assignmentService.assign(donationId, assignment).pipe(
      map((donation) => DonationsApiActions.assignmentSucceeded({ requestId, donation })),
      catchError((error: unknown) => of(DonationsApiActions.assignmentFailed({
        requestId,
        error: errorMessage(error, 'Unable to assign donation'),
      }))),
    )),
  ));

  readonly unassignDonation$ = createEffect(() => this.actions$.pipe(
    ofType(DonationsPageActions.unassignmentRequested),
    concatMap(({ requestId, donationId }) => this.assignmentService.unassign(donationId).pipe(
      map(() => DonationsApiActions.unassignmentSucceeded({ requestId, donationId })),
      catchError((error: unknown) => of(DonationsApiActions.unassignmentFailed({
        requestId,
        error: errorMessage(error, 'Unable to remove donation assignment'),
      }))),
    )),
  ));
}